import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMedia } from "../context/MediaContext";
import { uploadMedia, resolveMedia, apiError } from "../lib/api";

// A swappable image. Admins can click "Replace" to upload their own; everyone sees the current image.
export default function EditableImage({ id, src, alt = "", className = "", imgClassName = "" }) {
  const { user } = useAuth();
  const { get, set } = useMedia();
  const isAdmin = user && user.role === "admin";
  const current = resolveMedia(get(id, src));
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadMedia(id, file);
      set(id, url);
      toast.success("Image updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className={`relative group/img ${className}`}>
      <img src={current} alt={alt} className={imgClassName || "h-full w-full object-cover"} />
      {isAdmin && (
        <>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onFile} data-testid={`media-input-${id}`} />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`media-replace-${id}`}
            className="absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-nead-ink/85 px-3 py-2 text-xs font-semibold text-white opacity-0 group-hover/img:opacity-100 transition-opacity backdrop-blur hover:bg-nead-pink"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Replace
          </button>
        </>
      )}
    </div>
  );
}
